import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Plus,
  Database,
  Table,
  Search,
  Terminal,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';
import { datasetApi } from '../api/datasetApi';
import { bigqueryApi } from '../api/bigqueryApi';
import { udfApi } from '../api/udfApi';
import ConnectModal from '../components/workspace/ConnectModal';
import ConfirmModal from '../components/common/ConfirmModal';
import PreviewTable from '../components/workspace/PreviewTable';
import BillingProjectModal from '../components/workspace/BillingProjectModal';
import FunctionDetailModal from '../components/functions/FunctionDetailModal';
import FunctionImportModal from '../components/functions/FunctionImportModal';
import TreeTable from '../components/common/TreeTable';
import { cn } from '../lib/utils';
import { showErrorToast, showInfoToast, showSuccessToast } from '../lib/toast';
import {
  buildOptimisticDataSourcesDataset,
  isLocalDatasetType,
  isUploadDatasetType,
} from '../utils/datasetDisplayUtils';
import { useAuth } from '../context/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  FEATURE_INTEGRATION_BIGQUERY,
  FEATURE_INTEGRATION_UDF
} from '../constants/capabilities';

const MANTEO_WAREHOUSE_KEY = 'manteo-warehouse';
const DATA_CATALOG_SUBGROUP_KEY = 'data-catalog';
const USER_DATA_SUBGROUP_KEY = 'user-data';

const collectLocalGroupExpansionKeys = localGroups => {
  const keys = new Set();
  Object.entries(localGroups).forEach(([parentKey, value]) => {
    if (Array.isArray(value)) {
      keys.add(parentKey);
      return;
    }
    if (value && typeof value === 'object') {
      keys.add(parentKey);
      Object.keys(value).forEach(subKey => keys.add(`${parentKey}.${subKey}`));
    }
  });
  return keys;
};

const DataSourcesPage = () => {
  const { capabilities, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    document.title = 'manteo.ai | Data Sources';
  }, []);
  const [bqStatus, setBqStatus] = useState({ connected: false });
  const [datasets, setDatasets] = useState([]);
  const [functions, setFunctions] = useState([]);
  const [isConnectModalOpen, setIsConnectModalOpen] = useState(false);
  const [isFunctionImportModalOpen, setIsFunctionImportModalOpen] = useState(false);
  const [isBillingModalOpen, setIsBillingModalOpen] = useState(false);
  const [resumeAfterBilling, setResumeAfterBilling] = useState(null); // null | 'dataset' | 'function'
  const [modalMode, setModalMode] = useState('table'); // 'table' | 'connection'
  const [connectInitialStep, setConnectInitialStep] = useState('select');

  // Preview modal state
  const [previewDataset, setPreviewDataset] = useState(null);
  const [selectedFunction, setSelectedFunction] = useState(null);
  const [isPolling, setIsPolling] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [datasetSearchQuery, setDatasetSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('datasets');

  // Sorting state
  const [sortColumn, setSortColumn] = useState('created_at'); // 'name' | 'created_at'
  const [sortDirection, setSortDirection] = useState('desc'); // 'asc' | 'desc'

  // Tree expansion state
  const [expandedProjects, setExpandedProjects] = useState(new Set());
  const [expandedDatasets, setExpandedDatasets] = useState(new Set());
  const [expandedLocalGroups, setExpandedLocalGroups] = useState(new Set());
  const hasInitializedLibraryExpansion = useRef(false);
  const handledPreviewDatasetIdRef = useRef(null);
  const initialSourcesFetchDoneRef = useRef(false);

  const [isSourcesLoading, setIsSourcesLoading] = useState(true);

  // Function tree expansion state
  const [expandedFunctionProjects, setExpandedFunctionProjects] = useState(new Set());
  const [expandedFunctionDatasets, setExpandedFunctionDatasets] = useState(new Set());

  // Function sorting state
  const [functionSortColumn, setFunctionSortColumn] = useState('created_at'); // 'name' | 'created_at'
  const [functionSortDirection, setFunctionSortDirection] = useState('desc'); // 'asc' | 'desc'

  // Confirmation modal states
  const [confirmConfig, setConfirmConfig] = useState({
    isOpen: false,
    title: '',
    description: '',
    onConfirm: () => { },
    variant: 'danger'
  });
  const [removingDatasetId, setRemovingDatasetId] = useState(null);
  const bigqueryEnabled = capabilities?.features?.[FEATURE_INTEGRATION_BIGQUERY] !== false;
  const udfEnabled = capabilities?.features?.[FEATURE_INTEGRATION_UDF] !== false;

  const loadData = useCallback(async () => {
    try {
      const [bq, ds, udfs] = await Promise.all([
        bigqueryEnabled
          ? bigqueryApi.getStatus()
          : Promise.resolve({ connected: false, capability_disabled: true }),
        datasetApi.getDatasets(),
        udfEnabled ? udfApi.listUDFs() : Promise.resolve([])
      ]);
      setBqStatus(bq || { connected: false });
      setDatasets(ds || []);
      setFunctions(udfs || []);
    } catch (err) {
      console.error('Failed to load data sources:', err);
    } finally {
      if (!initialSourcesFetchDoneRef.current) {
        initialSourcesFetchDoneRef.current = true;
        setIsSourcesLoading(false);
      }
    }
  }, [bigqueryEnabled, udfEnabled]);

  useEffect(() => {
    loadData();

    // Handle Google OAuth callback
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('bigquery-connected')) {
      // Clear the param from URL
      window.history.replaceState({}, document.title, window.location.pathname);

      // We'll use a local check once data is loaded to trigger the modal
      const finalizeConnection = async () => {
        const bq = bigqueryEnabled
          ? await bigqueryApi.getStatus()
          : { connected: false, capability_disabled: true };
        if (bq.connected && !bq.billing_project_id) {
          handleOpenSettings();
        }
      };
      finalizeConnection();
    }
  }, [loadData, bigqueryEnabled]);

  useEffect(() => {
    const hasPending = datasets.some(d => d.status === 'loading' || d.status === 'generating');
    if (hasPending && !isPolling) {
      setIsPolling(true);
    } else if (!hasPending && isPolling) {
      setIsPolling(false);
    }
  }, [datasets, isPolling]);

  useEffect(() => {
    let interval;
    if (isPolling) {
      interval = setInterval(() => {
        loadData();
      }, 3000);
    }
    return () => clearInterval(interval);
  }, [isPolling, loadData]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const previewDatasetId = params.get('previewDataset');

    if (!previewDatasetId) {
      handledPreviewDatasetIdRef.current = null;
      return;
    }

    if (handledPreviewDatasetIdRef.current === previewDatasetId) {
      return;
    }

    const targetDataset = datasets.find(d => String(d.id) === previewDatasetId);
    if (!targetDataset) {
      return;
    }

    handledPreviewDatasetIdRef.current = previewDatasetId;
    if (targetDataset.status === 'ready') {
      setPreviewDataset({ id: targetDataset.id, name: targetDataset.name });
    } else if (targetDataset.status === 'failed') {
      setPreviewDataset({ id: targetDataset.id, name: targetDataset.name });
    } else {
      showInfoToast('Dataset preview is not ready yet. Please wait for indexing to finish.');
    }

    params.delete('previewDataset');
    const nextSearch = params.toString();
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : ''
      },
      { replace: true }
    );
  }, [datasets, location.pathname, location.search, navigate]);

  const handleConnect = async (type, data) => {
    if (type === 'upload' && data?.table_id) {
      const tableId = data.table_id;
      setDatasets((prev) => [
        buildOptimisticDataSourcesDataset(tableId),
        ...prev.filter((d) => d.id !== tableId),
      ]);
      showSuccessToast('Dataset added.');
      loadData().catch((err) => {
        console.error('Failed to refresh datasets after upload:', err);
        showErrorToast('Dataset was created but the list could not be refreshed.');
      });
      return;
    }

    if (type === 'bigquery' && data) {
      try {
        await datasetApi.importBigQueryTables(data, null, { suppressGlobalErrorToast: true });
        showInfoToast('Import started. New datasets will appear shortly.');
      } catch (err) {
        console.error('Failed to import BigQuery tables:', err);
        showErrorToast('Could not import selected tables. Please try again.');
      }
    }
    await loadData();

    if (type === 'sample' || type === 'library') {
      showSuccessToast('Dataset added.');
    }
  };

  const handleDownloadDataset = async (id, dataset) => {
    try {
      await datasetApi.downloadParquet(id, dataset?.displayName || dataset?.name);
      showSuccessToast('Parquet download started.');
    } catch (err) {
      console.error('Failed to download dataset:', err);
      showErrorToast(
        err.response?.data?.detail || err.message || 'Could not download Parquet file.'
      );
    }
  };

  const handleRemoveDataset = async (id, dataset) => {
    if (removingDatasetId) return;
    if (dataset?.status === 'loading' || dataset?.status === 'generating') {
      return;
    }
    const description = dataset?.source === 'bigquery'
      ? 'This will remove the dataset from your platform library. The original in BigQuery will not be affected.'
      : 'This will remove the dataset from your platform library.';

    setConfirmConfig({
      isOpen: true,
      title: 'Remove Dataset?',
      description,
      variant: 'danger',
      onConfirm: async () => {
        setConfirmConfig((prev) => ({ ...prev, isOpen: false }));
        setRemovingDatasetId(id);
        try {
          await datasetApi.deleteDataset(id);
          if (previewDataset?.id === id) {
            setPreviewDataset(null);
          }
          await loadData();
          showSuccessToast('Dataset removed.');
        } catch (err) {
          console.error('Failed to remove dataset:', err);
          showErrorToast(
            err?.response?.data?.detail || 'Failed to remove dataset.'
          );
        } finally {
          setRemovingDatasetId(null);
        }
      }
    });
  };

  const handleRemoveFunction = async (id) => {
    setConfirmConfig({
      isOpen: true,
      title: 'Remove Function?',
      description: 'This will remove the function from your platform library. The original in BigQuery will not be affected.',
      variant: 'danger',
      onConfirm: async () => {
        try {
          await udfApi.deleteUDF(id);
          await loadData();
          showSuccessToast('Function removed.');
          if (selectedFunction?.id === id) {
            setSelectedFunction(null);
          }
        } catch (err) {
          console.error('Failed to remove function:', err);
        }
      }
    });
  };

  const handleRebuildDataset = async (id) => {
    try {
      await datasetApi.rebuildMetadata(id);
      await loadData();
      setIsPolling(true); // Start polling for status updates
      showInfoToast('Metadata refresh started.');
    } catch (err) {
      console.error('Failed to rebuild dataset metadata:', err);
    }
  };

  const openAddTable = () => {
    setModalMode('table');
    setConnectInitialStep('select');
    setIsConnectModalOpen(true);
  };

  const openAddConnection = () => {
    setModalMode('connection');
    setConnectInitialStep('select');
    setIsConnectModalOpen(true);
  };

  const openAddBigQueryFromSource = () => {
    setModalMode('table');
    setConnectInitialStep('bigquery-dataset');
    setIsConnectModalOpen(true);
  };

  const openBigQueryReconnect = useCallback(() => {
    setModalMode('connection');
    setConnectInitialStep('bigquery-auth');
    setIsConnectModalOpen(true);
  }, []);

  const handleOpenSettings = () => {
    setIsBillingModalOpen(true);
  };

  const handleDisconnectBigQuery = () => {
    setConfirmConfig({
      isOpen: true,
      title: 'Unlink BigQuery Connection?',
      description: 'This will disconnect your BigQuery account from Manteo and remove all imported BigQuery tables from your platform library.',
      variant: 'danger',
      onConfirm: async () => {
        try {
          await bigqueryApi.disconnect();
          await loadData();
          showSuccessToast('BigQuery connection unlinked.');
        } catch (err) {
          console.error('Failed to unlink BigQuery connection:', err);
          showErrorToast('Could not unlink BigQuery connection. Please try again.');
        }
      }
    });
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (!params.get('bigquery-reauth')) return;

    openBigQueryReconnect();
    params.delete('bigquery-reauth');
    navigate(
      {
        pathname: location.pathname,
        search: params.toString() ? `?${params.toString()}` : ''
      },
      { replace: true }
    );
  }, [location.pathname, location.search, navigate, openBigQueryReconnect]);

  // Filter and sort functions
  const filteredFunctions = useMemo(() => {
    let filtered = functions.filter(func => {
      const q = searchQuery.toLowerCase();
      const matchesSearch = func.name.toLowerCase().includes(q) ||
        (func.description && func.description.toLowerCase().includes(q)) ||
        (func.signature && func.signature.toLowerCase().includes(q));
      return matchesSearch;
    });

    // Sort
    filtered.sort((a, b) => {
      let aVal, bVal;

      switch (functionSortColumn) {
        case 'name':
          aVal = a.name.toLowerCase();
          bVal = b.name.toLowerCase();
          break;
        case 'created_at':
          aVal = a.created_at ? new Date(a.created_at).getTime() : 0;
          bVal = b.created_at ? new Date(b.created_at).getTime() : 0;
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return functionSortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return functionSortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [functions, searchQuery, functionSortColumn, functionSortDirection]);

  // Build BigQuery functions tree structure (project -> dataset -> functions)
  const bigQueryFunctionsTree = useMemo(() => {
    const tree = {};
    const bqFunctions = filteredFunctions.filter(f => f.source === 'bigquery' && f.routine_id);

    bqFunctions.forEach(func => {
      // Parse routine_id (project.dataset.function_name)
      const parts = func.routine_id.split('.');
      if (parts.length >= 3) {
        const project = parts[0];
        const dataset = parts[1];
        const functionName = parts.slice(2).join('.');

        if (!tree[project]) {
          tree[project] = {};
        }
        if (!tree[project][dataset]) {
          tree[project][dataset] = [];
        }
        tree[project][dataset].push({
          ...func,
          project,
          dataset,
          functionName
        });
      }
    });

    if (bqStatus?.connected) {
      const connectedProject = bqStatus.billing_project_id || 'BigQuery';
      if (!tree[connectedProject]) {
        tree[connectedProject] = {};
      }
    }

    return tree;
  }, [filteredFunctions, bqStatus]);

  // Separate custom functions
  const customFunctions = useMemo(() => {
    return filteredFunctions.filter(f => f.source === 'custom');
  }, [filteredFunctions]);

  // Flatten datasets for table view and filter
  const filteredDatasets = useMemo(() => {
    const allTables = [];
    const sourcesList = Array.isArray(datasets) ? datasets : [];

    sourcesList.forEach(d => {
      const metadataFields = {
        stats: d.stats || {},
        column_count: d.column_count ?? (Array.isArray(d.schema) ? d.schema.length : 0),
        has_spatial: d.has_spatial ?? (Array.isArray(d.spatial_columns) && d.spatial_columns.length > 0),
        has_temporal: d.has_temporal ?? (Array.isArray(d.temporal_columns) && d.temporal_columns.length > 0),
        summary: d.summary ?? null,
      };

      if (isLocalDatasetType(d.type)) {
        allTables.push({
          id: d.id,
          name: d.name,
          type: d.type,
          status: d.status,
          source: 'manteo',
          displayName: d.name,
          ...metadataFields,
          created_at: d.created_at
        });
      } else if (d.type === 'bigquery') {
        // Parse BigQuery table ID (project.dataset.table)
        const parts = d.id.split('.');
        if (parts.length === 3) {
          const [project, dataset, table] = parts;
          allTables.push({
            id: d.id,
            name: table,
            type: d.type,
            status: d.status,
            source: 'bigquery',
            project: project,
            dataset: dataset,
            displayName: `${project}.${dataset}.${table}`,
            ...metadataFields,
            created_at: d.created_at
          });
        } else {
          // Fallback for non-standard IDs
          allTables.push({
            id: d.id,
            name: d.name || d.id,
            type: d.type,
            status: d.status,
            source: 'bigquery',
            displayName: d.name || d.id,
            ...metadataFields,
            created_at: d.created_at
          });
        }
      }
    });

    // Filter by search query
    const q = datasetSearchQuery.toLowerCase();
    let filtered = allTables.filter(table =>
      table.displayName.toLowerCase().includes(q) ||
      table.name.toLowerCase().includes(q) ||
      (table.project && table.project.toLowerCase().includes(q)) ||
      (table.dataset && table.dataset.toLowerCase().includes(q)) ||
      (table.summary && String(table.summary).toLowerCase().includes(q))
    );

    // Sort
    filtered.sort((a, b) => {
      let aVal, bVal;

      switch (sortColumn) {
        case 'name':
          aVal = a.name.toLowerCase();
          bVal = b.name.toLowerCase();
          break;
        case 'created_at':
          aVal = a.created_at ? new Date(a.created_at).getTime() : 0;
          bVal = b.created_at ? new Date(b.created_at).getTime() : 0;
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [datasets, datasetSearchQuery, sortColumn, sortDirection]);

  // Build BigQuery tree structure (project -> dataset -> tables)
  const bigQueryTree = useMemo(() => {
    const tree = {};
    const bqTables = filteredDatasets.filter(t => t.source === 'bigquery' && t.project && t.dataset);

    bqTables.forEach(table => {
      if (!tree[table.project]) {
        tree[table.project] = {};
      }
      if (!tree[table.project][table.dataset]) {
        tree[table.project][table.dataset] = [];
      }
      tree[table.project][table.dataset].push(table);
    });

    if (bqStatus?.connected) {
      const connectedProject = bqStatus.billing_project_id || 'BigQuery';
      if (!tree[connectedProject]) {
        tree[connectedProject] = {};
      }
    }

    return tree;
  }, [filteredDatasets, bqStatus]);

  // Manteo cloud: data-catalog (samples) + user folder (uploads and other local datasets)
  const userFolderName = useMemo(
    () => user?.email || 'My data',
    [user]
  );

  const localDatasetsTree = useMemo(() => {
    const localTables = filteredDatasets.filter(t => t.source === 'manteo');
    const samples = localTables.filter(t => t.type === 'sample');
    const userDatasets = localTables.filter(t => t.type !== 'sample');

    return {
      [MANTEO_WAREHOUSE_KEY]: {
        [USER_DATA_SUBGROUP_KEY]: userDatasets,
        [DATA_CATALOG_SUBGROUP_KEY]: samples,
      },
    };
  }, [filteredDatasets]);

  useEffect(() => {
    if (hasInitializedLibraryExpansion.current) return;
    if (!localDatasetsTree[MANTEO_WAREHOUSE_KEY]) return;

    setExpandedLocalGroups(collectLocalGroupExpansionKeys(localDatasetsTree));
    hasInitializedLibraryExpansion.current = true;
  }, [localDatasetsTree]);

  // Check if we have datasets for each source
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const toggleLocalGroup = (groupKey) => {
    setExpandedLocalGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupKey)) {
        next.delete(groupKey);
      } else {
        next.add(groupKey);
      }
      return next;
    });
  };

  const toggleProject = (project) => {
    setExpandedProjects(prev => {
      const next = new Set(prev);
      if (next.has(project)) {
        next.delete(project);
      } else {
        next.add(project);
      }
      return next;
    });
  };

  const toggleDataset = (project, dataset) => {
    const key = `${project}.${dataset}`;
    setExpandedDatasets(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const toggleFunctionProject = (project) => {
    setExpandedFunctionProjects(prev => {
      const next = new Set(prev);
      if (next.has(project)) {
        next.delete(project);
      } else {
        next.add(project);
      }
      return next;
    });
  };

  const toggleFunctionDataset = (project, dataset) => {
    const key = `${project}.${dataset}`;
    setExpandedFunctionDatasets(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleFunctionSort = (column) => {
    if (functionSortColumn === column) {
      setFunctionSortDirection(functionSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setFunctionSortColumn(column);
      setFunctionSortDirection('asc');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'N/A';
    }
  };

  return (
    <div className="flex-1 overflow-y-auto bg-gray-50/50 px-8 py-8 transition-colors duration-300 dark:bg-gray-950">
      <ConnectModal
        isOpen={isConnectModalOpen}
        mode={modalMode}
        onClose={() => {
          setIsConnectModalOpen(false);
          setResumeAfterBilling(null);
          setConnectInitialStep('select');
        }}
        onConnect={handleConnect}
        initialStep={resumeAfterBilling === 'dataset' ? 'bigquery-dataset' : connectInitialStep}
        onBillingRequired={() => {
          setIsConnectModalOpen(false);
          setResumeAfterBilling('dataset');
          setIsBillingModalOpen(true);
        }}
        onConnectionUpdated={loadData}
        currentSources={datasets}
      />

      <FunctionImportModal
        isOpen={isFunctionImportModalOpen}
        onClose={() => setIsFunctionImportModalOpen(false)}
        importedFunctions={functions}
        onImportComplete={async () => {
          await loadData();
          setIsFunctionImportModalOpen(false);
          showSuccessToast('Functions imported.');
        }}
        onBillingRequired={() => {
          setIsFunctionImportModalOpen(false);
          setResumeAfterBilling('function');
          setIsBillingModalOpen(true);
        }}
      />

      <BillingProjectModal
        isOpen={isBillingModalOpen}
        onClose={() => {
          setIsBillingModalOpen(false);
          setResumeAfterBilling(null);
        }}
        onProjectSelected={async () => {
          await loadData();
          showSuccessToast('Billing project updated.');
          if (resumeAfterBilling === 'dataset') {
            setIsConnectModalOpen(true);
          } else if (resumeAfterBilling === 'function') {
            setResumeAfterBilling(null);
            setIsFunctionImportModalOpen(true);
          }
        }}
        currentBillingProject={bqStatus.billing_project_id}
      />

      <ConfirmModal
        isOpen={confirmConfig.isOpen}
        title={confirmConfig.title}
        description={confirmConfig.description}
        variant={confirmConfig.variant}
        onConfirm={confirmConfig.onConfirm}
        onClose={() => setConfirmConfig(prev => ({ ...prev, isOpen: false }))}
      />

      {previewDataset && (
        <PreviewTable
          dataset={previewDataset}
          onClose={() => setPreviewDataset(null)}
        />
      )}

      <FunctionDetailModal
        func={functions.find(f => f.id === selectedFunction?.id)}
        isOpen={!!selectedFunction}
        onClose={() => setSelectedFunction(null)}
        onUpdate={loadData}
      />

      <div className="mx-auto max-w-7xl space-y-5">
        <header className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
          <div>
            <h1 className="text-3xl font-black uppercase tracking-tight text-gray-900 dark:text-white">Data Sources</h1>
            <p className="mt-1 text-[10px] font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500">
              Manage Datasets, External Connections, and User Defined Functions.
            </p>
          </div>
        </header>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div
            role="tablist"
            aria-label="Data source library sections"
            className="inline-flex w-fit flex-wrap items-center gap-1 rounded-[22px] border border-gray-200/70 bg-white/70 p-1.5 shadow-inner shadow-gray-200/60 backdrop-blur dark:border-gray-800 dark:bg-gray-900/60 dark:shadow-black/20"
          >
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'datasets'}
              onClick={() => setActiveTab('datasets')}
              className={cn(
                "group inline-flex items-center gap-2 rounded-2xl px-4 py-2 font-black text-[10px] uppercase tracking-widest transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/40",
                activeTab === 'datasets'
                  ? "bg-white text-emerald-700 shadow-sm ring-1 ring-emerald-300 dark:bg-gray-800 dark:text-emerald-300 dark:ring-emerald-700/70"
                  : "text-gray-500 hover:bg-gray-100/80 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800/80 dark:hover:text-white"
              )}
            >
              <Database size={14} className="shrink-0" strokeWidth={2.5} />
              Datasets
              <span className={cn(
                "rounded-full px-1.5 py-0.5 text-[8px] leading-none transition-colors",
                activeTab === 'datasets'
                  ? "bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-300"
                  : "bg-gray-100 text-gray-500 group-hover:bg-white dark:bg-gray-800 dark:text-gray-400 dark:group-hover:bg-gray-700"
              )}>
                {filteredDatasets.length}
              </span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'functions'}
              onClick={() => setActiveTab('functions')}
              disabled={!udfEnabled}
              className={cn(
                "group inline-flex items-center gap-2 rounded-2xl px-4 py-2 font-black text-[10px] uppercase tracking-widest transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/40 disabled:focus-visible:ring-0",
                activeTab === 'functions'
                  ? "bg-white text-emerald-700 shadow-sm ring-1 ring-emerald-300 dark:bg-gray-800 dark:text-emerald-300 dark:ring-emerald-700/70"
                  : "text-gray-500 hover:bg-gray-100/80 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800/80 dark:hover:text-white",
                !udfEnabled && "cursor-not-allowed opacity-45 hover:bg-transparent hover:text-gray-500 dark:hover:bg-transparent dark:hover:text-gray-400"
              )}
            >
              <Terminal size={14} className="shrink-0" strokeWidth={2.5} />
              Functions
              {!udfEnabled ? (
                <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[8px] leading-none text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                  Pro
                </span>
              ) : (
                <span className={cn(
                  "rounded-full px-1.5 py-0.5 text-[8px] leading-none transition-colors",
                  activeTab === 'functions'
                    ? "bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-300"
                    : "bg-gray-100 text-gray-500 group-hover:bg-white dark:bg-gray-800 dark:text-gray-400 dark:group-hover:bg-gray-700"
                )}>
                  {filteredFunctions.length}
                </span>
              )}
            </button>
          </div>
        </div>

        <div className="animate-in fade-in slide-in-from-bottom-2 duration-500">
          {activeTab === 'datasets' ? (
            <section>
              <div className="bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-[24px] overflow-hidden shadow-sm min-h-[420px]">
                <div className="flex flex-col gap-3 border-b border-gray-100 p-3 dark:border-gray-800 md:flex-row md:items-center md:justify-between">
                  <div className="flex gap-2">
                    <button
                      onClick={openAddTable}
                      disabled={isSourcesLoading}
                      className="flex items-center gap-2 px-3.5 py-2 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-all font-black text-[9px] uppercase tracking-widest shadow-sm active:scale-95 disabled:pointer-events-none disabled:opacity-50"
                    >
                      <Plus size={14} strokeWidth={3} />
                      Add Dataset
                    </button>
                    <button
                      onClick={openAddConnection}
                      disabled={isSourcesLoading}
                      className="flex items-center gap-2 px-3.5 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all font-black text-[9px] uppercase tracking-widest shadow-sm active:scale-95 disabled:pointer-events-none disabled:opacity-50"
                    >
                      <Plus size={14} strokeWidth={3} />
                      Connect Source
                    </button>
                    {(Object.keys(bigQueryTree).length > 0 || Object.keys(localDatasetsTree).length > 0) && (
                      <div className="flex items-center gap-1 border-l border-gray-200 dark:border-gray-700 pl-2 ml-2">
                        <button
                          onClick={() => {
                            // Expand all
                            const allProjects = Object.keys(bigQueryTree);
                            const allDatasets = new Set();
                            allProjects.forEach(project => {
                              setExpandedProjects(prev => new Set([...prev, project]));
                              Object.keys(bigQueryTree[project]).forEach(dataset => {
                                allDatasets.add(`${project}.${dataset}`);
                              });
                            });
                            setExpandedDatasets(allDatasets);
                            setExpandedLocalGroups(collectLocalGroupExpansionKeys(localDatasetsTree));
                          }}
                          className="px-2 py-1 text-[8px] font-black uppercase tracking-widest text-gray-500 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors"
                          title="Expand All"
                        >
                          Expand All
                        </button>
                        <span className="text-gray-300 dark:text-gray-600">|</span>
                        <button
                          onClick={() => {
                            // Collapse all
                            setExpandedProjects(new Set());
                            setExpandedDatasets(new Set());
                            setExpandedLocalGroups(new Set());
                          }}
                          className="px-2 py-1 text-[8px] font-black uppercase tracking-widest text-gray-500 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors"
                          title="Collapse All"
                        >
                          Collapse All
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="group relative w-full md:w-[280px]">
                    <Search
                      size={14}
                      strokeWidth={2.5}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 transition-colors group-focus-within:text-emerald-500"
                    />
                    <input
                      type="text"
                      placeholder="Search datasets..."
                      value={datasetSearchQuery}
                      onChange={(e) => setDatasetSearchQuery(e.target.value)}
                      className="w-full rounded-xl border border-gray-100 bg-white py-2 pl-10 pr-4 text-[10px] font-black tracking-widest shadow-sm transition-all focus:border-emerald-500/50 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 dark:border-gray-800 dark:bg-gray-900"
                    />
                  </div>
                </div>
                {isSourcesLoading ? (
                  <div
                    className="flex min-h-[320px] flex-col items-center justify-center gap-4 py-16"
                    role="status"
                    aria-live="polite"
                    aria-busy="true"
                  >
                    <div className="h-8 w-8 animate-spin rounded-full border-2 border-emerald-500/20 border-t-emerald-500" />
                    <span className="text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-gray-500">
                      Loading data sources…
                    </span>
                  </div>
                ) : (filteredDatasets.length === 0 && (!bqStatus?.connected || datasetSearchQuery.trim())) ? (
                  <div className="flex flex-col items-center justify-center py-24 text-center">
                    <div className="p-6 bg-gray-50 dark:bg-gray-800/50 rounded-[32px] shadow-sm mb-5 border border-gray-100 dark:border-gray-800">
                      <Database size={44} strokeWidth={1} className="text-gray-200 dark:text-gray-700" />
                    </div>
                    <h3 className="text-xs font-black text-gray-400 dark:text-gray-500 uppercase tracking-[0.2em]">
                      {datasetSearchQuery ? 'No matches found' : 'No datasets registered'}
                    </h3>
                    <p className="text-[10px] text-gray-400 dark:text-gray-600 mt-2 max-w-[300px] leading-relaxed">
                      {datasetSearchQuery
                        ? 'Try adjusting your search or filters'
                        : 'Import datasets to index them and make them smart and understandable to AI agents. Datasets are not copied.'}
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[900px] text-left border-collapse">
                      <thead>
                        <tr className="bg-gray-50/50 dark:bg-gray-950/50 border-b border-gray-100 dark:border-gray-800">
                          <th
                            className="px-6 py-3 text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 dark:text-gray-500 cursor-pointer hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors"
                            onClick={() => handleSort('name')}
                          >
                            <div className="flex items-center gap-2">
                              Dataset Name
                              {sortColumn === 'name' ? (
                                sortDirection === 'asc' ? <ArrowUp size={11} /> : <ArrowDown size={11} />
                              ) : (
                                <ArrowUpDown size={11} className="opacity-30" />
                              )}
                            </div>
                          </th>
                          <th className="px-5 py-3 text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 dark:text-gray-500">
                            Rows
                          </th>
                          <th className="px-5 py-3 text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 dark:text-gray-500">
                            Columns
                          </th>
                          <th className="px-5 py-3 text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 dark:text-gray-500">
                            Spatial
                          </th>
                          <th className="px-5 py-3 text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 dark:text-gray-500">
                            Temporal
                          </th>
                          <th
                            className="px-6 py-3 text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 dark:text-gray-500 cursor-pointer hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors"
                            onClick={() => handleSort('created_at')}
                          >
                            <div className="flex items-center gap-2">
                              Added At
                              {sortColumn === 'created_at' ? (
                                sortDirection === 'asc' ? <ArrowUp size={11} /> : <ArrowDown size={11} />
                              ) : (
                                <ArrowUpDown size={11} className="opacity-30" />
                              )}
                            </div>
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50 dark:divide-gray-800/30">
                        <TreeTable
                          bigQueryTree={bigQueryTree}
                          localGroups={localDatasetsTree}
                          bigQueryReauthRequired={Boolean(bqStatus?.reauth_required)}
                          expandedProjects={expandedProjects}
                          expandedDatasets={expandedDatasets}
                          expandedLocalGroups={expandedLocalGroups}
                          onToggleProject={toggleProject}
                          onToggleDataset={toggleDataset}
                          onToggleLocalGroup={toggleLocalGroup}
                          onProjectAdd={() => openAddBigQueryFromSource()}
                          onLocalSubgroupAdd={(_parentKey, subKey) => {
                            if (subKey === DATA_CATALOG_SUBGROUP_KEY) return;
                            openAddTable();
                          }}
                          getLocalGroupLabel={groupKey =>
                            groupKey === MANTEO_WAREHOUSE_KEY ? 'Manteo cloud' : groupKey
                          }
                          getLocalSubgroupLabel={(_parentKey, subKey) => {
                            if (subKey === DATA_CATALOG_SUBGROUP_KEY) return 'data-catalog';
                            if (subKey === USER_DATA_SUBGROUP_KEY) return userFolderName;
                            return subKey;
                          }}
                          getLocalSubgroupEmptyMessage={(parentKey, subKey) => {
                            if (parentKey !== MANTEO_WAREHOUSE_KEY || subKey !== USER_DATA_SUBGROUP_KEY) {
                              return null;
                            }
                            if (datasetSearchQuery.trim()) return null;
                            if (datasets.some(d => isUploadDatasetType(d.type))) return null;
                            return 'No user datasets yet. Please upload a dataset.';
                          }}
                          onSelectItem={(item) => {
                            if (item.status === 'ready' || item.status === 'failed') {
                              setPreviewDataset({ id: item.id, name: item.name });
                            }
                          }}
                          onRemoveItem={handleRemoveDataset}
                          removingItemId={removingDatasetId}
                          onDownloadItem={handleDownloadDataset}
                          onRetryItem={handleRebuildDataset}
                          formatDate={formatDate}
                          itemIcon={Table}
                          projectIconColor="text-blue-500"
                          datasetIconColor="text-blue-400"
                          localGroupIconColor="text-emerald-500"
                          showStatus={true}
                          showMetadataColumns={true}
                          onProjectSettings={() => handleOpenSettings()}
                          onProjectUnlink={() => handleDisconnectBigQuery()}
                          onProjectReauth={() => openBigQueryReconnect()}
                          getItemStatus={(item) => item.status}
                          getDisplayName={(item) => item.displayName}
                          uppercaseItemName={false}
                          isBigQueryLocked={!bigqueryEnabled}
                        />
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </section>
          ) : (
            <section className="space-y-3">
              {!udfEnabled && (
                <div className="rounded-2xl border border-yellow-200 dark:border-yellow-800/50 bg-yellow-50 dark:bg-yellow-900/10 p-4">
                  <h3 className="text-sm font-black text-yellow-900 dark:text-yellow-100 uppercase tracking-widest">
                    Function Add-on Required
                  </h3>
                  <p className="text-xs text-yellow-800 dark:text-yellow-200 mt-2">
                    Your current plan does not include User Defined Functions.
                  </p>
                </div>
              )}
              <div className="bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-[24px] overflow-hidden shadow-sm min-h-[420px]">
                <div className="flex flex-col gap-3 border-b border-gray-100 p-3 dark:border-gray-800 md:flex-row md:items-center md:justify-between">
                  <div className="flex gap-2">
                    <button
                      onClick={() => setIsFunctionImportModalOpen(true)}
                      disabled={!udfEnabled || isSourcesLoading}
                      className="flex items-center gap-2 px-3.5 py-2 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-all font-black text-[9px] uppercase tracking-widest shadow-sm active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Plus size={14} strokeWidth={3} />
                      Add Function
                    </button>
                    {(Object.keys(bigQueryFunctionsTree).length > 0 || customFunctions.length > 0) && (
                      <div className="flex items-center gap-1 border-l border-gray-200 dark:border-gray-700 pl-2 ml-2">
                        <button
                          onClick={() => {
                            // Expand all
                            const allProjects = Object.keys(bigQueryFunctionsTree);
                            const allDatasets = new Set();
                            allProjects.forEach(project => {
                              setExpandedFunctionProjects(prev => new Set([...prev, project]));
                              Object.keys(bigQueryFunctionsTree[project]).forEach(dataset => {
                                allDatasets.add(`${project}.${dataset}`);
                              });
                            });
                            setExpandedFunctionDatasets(allDatasets);
                          }}
                          className="px-2 py-1 text-[8px] font-black uppercase tracking-widest text-gray-500 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors"
                          title="Expand All"
                        >
                          Expand All
                        </button>
                        <span className="text-gray-300 dark:text-gray-600">|</span>
                        <button
                          onClick={() => {
                            // Collapse all
                            setExpandedFunctionProjects(new Set());
                            setExpandedFunctionDatasets(new Set());
                          }}
                          className="px-2 py-1 text-[8px] font-black uppercase tracking-widest text-gray-500 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors"
                          title="Collapse All"
                        >
                          Collapse All
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="group relative w-full md:w-[280px]">
                    <Search
                      size={14}
                      strokeWidth={2.5}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 transition-colors group-focus-within:text-emerald-500"
                    />
                    <input
                      type="text"
                      placeholder="Search functions..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      disabled={!udfEnabled}
                      className="w-full rounded-xl border border-gray-100 bg-white py-2 pl-10 pr-4 text-[10px] font-black tracking-widest shadow-sm transition-all focus:border-emerald-500/50 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-800 dark:bg-gray-900"
                    />
                  </div>
                </div>
                {!udfEnabled ? (
                  <div className="flex flex-col items-center justify-center py-24 text-center">
                    <div className="p-6 bg-gray-50 dark:bg-gray-800/50 rounded-[32px] shadow-sm mb-5 border border-gray-100 dark:border-gray-800">
                      <Terminal size={44} strokeWidth={1} className="text-gray-200 dark:text-gray-700" />
                    </div>
                    <h3 className="text-xs font-black text-gray-400 dark:text-gray-500 uppercase tracking-[0.2em]">
                      Functions are disabled for this plan
                    </h3>
                  </div>
                ) : isSourcesLoading ? (
                  <div
                    className="flex min-h-[320px] flex-col items-center justify-center gap-4 py-16"
                    role="status"
                    aria-live="polite"
                    aria-busy="true"
                  >
                    <div className="h-8 w-8 animate-spin rounded-full border-2 border-emerald-500/20 border-t-emerald-500" />
                    <span className="text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-gray-500">
                      Loading data sources…
                    </span>
                  </div>
                ) : filteredFunctions.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-24 text-center">
                    <div className="p-6 bg-gray-50 dark:bg-gray-800/50 rounded-[32px] shadow-sm mb-5 border border-gray-100 dark:border-gray-800">
                      <Terminal size={44} strokeWidth={1} className="text-gray-200 dark:text-gray-700" />
                    </div>
                    <h3 className="text-xs font-black text-gray-400 dark:text-gray-500 uppercase tracking-[0.2em]">
                      {searchQuery ? 'No matches found' : 'No functions registered'}
                    </h3>
                    <p className="text-[10px] text-gray-400 dark:text-gray-600 mt-2 max-w-[300px] leading-relaxed">
                      {searchQuery
                        ? 'Try adjusting your search or filters'
                        : 'Import functions to index them and make them smart and understandable to AI agents.'}
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="bg-gray-50/50 dark:bg-gray-950/50 border-b border-gray-100 dark:border-gray-800">
                          <th
                            className="px-6 py-3 text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 dark:text-gray-500 cursor-pointer hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors"
                            onClick={() => handleFunctionSort('name')}
                          >
                            <div className="flex items-center gap-2">
                              Function Name
                              {functionSortColumn === 'name' ? (
                                functionSortDirection === 'asc' ? <ArrowUp size={11} /> : <ArrowDown size={11} />
                              ) : (
                                <ArrowUpDown size={11} className="opacity-30" />
                              )}
                            </div>
                          </th>
                          <th
                            className="px-6 py-3 text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 dark:text-gray-500 cursor-pointer hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors"
                            onClick={() => handleFunctionSort('created_at')}
                          >
                            <div className="flex items-center gap-2">
                              Added At
                              {functionSortColumn === 'created_at' ? (
                                functionSortDirection === 'asc' ? <ArrowUp size={11} /> : <ArrowDown size={11} />
                              ) : (
                                <ArrowUpDown size={11} className="opacity-30" />
                              )}
                            </div>
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50 dark:divide-gray-800/30">
                        <TreeTable
                          bigQueryTree={bigQueryFunctionsTree}
                          customGroup={customFunctions}
                          bigQueryReauthRequired={Boolean(bqStatus?.reauth_required)}
                          expandedProjects={expandedFunctionProjects}
                          expandedDatasets={expandedFunctionDatasets}
                          onToggleProject={toggleFunctionProject}
                          onToggleDataset={toggleFunctionDataset}
                          onProjectAdd={() => setIsFunctionImportModalOpen(true)}
                          onSelectItem={setSelectedFunction}
                          onRemoveItem={handleRemoveFunction}
                          formatDate={formatDate}
                          itemIcon={Terminal}
                          projectIconColor="text-blue-500"
                          datasetIconColor="text-blue-400"
                          localGroupIconColor="text-emerald-500"
                          showStatus={false}
                          onProjectSettings={() => handleOpenSettings()}
                          onProjectUnlink={() => handleDisconnectBigQuery()}
                          onProjectReauth={() => openBigQueryReconnect()}
                          getItemName={(item) => item.functionName || item.name}
                          getDisplayName={(item) => item.signature || item.name}
                          isBigQueryLocked={!bigqueryEnabled}
                        />
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default DataSourcesPage;
